import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardLayout } from '../components/WizardLayout';
import { useBookingStore } from '../store/bookingStore';
import { publicApi } from '../lib/api';
import { User, Mail, Phone, MessageSquare, Plus, Minus, Plane, Luggage } from 'lucide-react';
import { toast } from 'sonner';
import * as yup from 'yup';
import { useLanguage } from '../locales/LanguageContext';

export const Step2_ServiceDetails = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const selectedVehicle = useBookingStore(state => state.selectedVehicle);
  const customerInfo = useBookingStore(state => state.customerInfo);
  const notes = useBookingStore(state => state.notes);
  const flightNumber = useBookingStore(state => state.flightNumber);
  const luggageCount = useBookingStore(state => state.luggageCount);
  const selectedAddons = useBookingStore(state => state.selectedAddons);
  const setCustomerInfo = useBookingStore(state => state.setCustomerInfo);
  const setNotes = useBookingStore(state => state.setNotes);
  const setFlightNumber = useBookingStore(state => state.setFlightNumber);
  const setLuggageCount = useBookingStore(state => state.setLuggageCount);
  const setSelectedAddons = useBookingStore(state => state.setSelectedAddons);
  const nextStep = useBookingStore(state => state.nextStep);
  
  const schema = useMemo(() => yup.object().shape({
    full_name: yup.string().required(t('step2.validation.name_required')).min(3, t('step2.validation.name_min')),
    email: yup.string().email(t('step2.validation.email_invalid')).required(t('step2.validation.email_required')),
    confirm_email: yup.string()
      .required(t('step2.validation.confirm_email_required'))
      .oneOf([yup.ref('email')], t('step2.validation.emails_not_match')),
    phone: yup.string()
      .required(t('step2.validation.phone_required'))
      .matches(/^\d{10,15}$/, t('step2.validation.phone_format')),
    confirm_phone: yup.string()
      .required(t('step2.validation.confirm_phone_required'))
      .oneOf([yup.ref('phone')], t('step2.validation.phones_not_match')),
    flight_number: yup.string()
      .matches(/^$|^[A-Za-z0-9]{2,10}$/, t('step2.validation.flight_number_format')),
    luggage_count: yup.number()
      .typeError(t('step2.validation.luggage_count_required'))
      .required(t('step2.validation.luggage_count_required'))
      .integer(t('step2.validation.luggage_count_range'))
      .min(0, t('step2.validation.luggage_count_range'))
      .max(20, t('step2.validation.luggage_count_range'))
  }), [t]);

  const [availableAddons, setAvailableAddons] = useState([]);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({ ...customerInfo, confirm_email: '', confirm_phone: '', flight_number: flightNumber, luggage_count: luggageCount });
  
  useEffect(() => {
    if (!selectedVehicle) {
      navigate('/booking');
    }
    loadAddons();
  }, [selectedVehicle, navigate]);
  
  const loadAddons = async () => {
    try {
      const response = await publicApi.getAddons();
      setAvailableAddons(response.data);
    } catch (error) {
      console.error('Error loading addons:', error);
      toast.error(t('step2.validation.form_errors'));
    }
  };
  
  const handleAddonChange = (addon, quantity) => {
    const existing = selectedAddons.find(a => a.addon_id === addon.id);
    
    if (quantity === 0) {
      setSelectedAddons(selectedAddons.filter(a => a.addon_id !== addon.id));
    } else if (existing) {
      setSelectedAddons(selectedAddons.map(a => 
        a.addon_id === addon.id ? { ...a, quantity } : a
      ));
    } else {
      setSelectedAddons([...selectedAddons, { addon_id: addon.id, quantity, price: addon.price, name: addon.name, type: addon.type || 'PER_TRIP' }]);
    }
  };
  
  const getAddonQuantity = (addonId) => {
    const addon = selectedAddons.find(a => a.addon_id === addonId);
    return addon ? addon.quantity : 0;
  };
  
  const handleNext = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      const { confirm_email, confirm_phone, flight_number, luggage_count, ...customerData } = formData;
      setCustomerInfo(customerData);
      setFlightNumber(flight_number || '');
      setLuggageCount(parseInt(luggage_count) || 0);

      // If a reservation already exists, update it with current addons
      const reservationCreated = useBookingStore.getState().reservationCreated;
      if (reservationCreated && reservationCreated.reservation_id) {
        try {
          const addonsPayload = selectedAddons.map(a => ({ addon_id: a.addon_id, quantity: a.quantity }));
          const res = await publicApi.updateReservationAddons(reservationCreated.reservation_id, addonsPayload);
          useBookingStore.getState().setReservationCreated(res.data);
        } catch (err) {
          console.error('Error updating addons:', err);
        }
      }

      nextStep();
      navigate('/booking/confirmation');
    } catch (err) {
      const validationErrors = {};
      err.inner.forEach(error => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      toast.error(t('step2.validation.form_errors'));
    }
  };
  
  if (!selectedVehicle) return null;
  
  return (
    <WizardLayout 
      onNext={handleNext}
      canGoNext={true}
    >
      <div data-testid="step-service-details">
        <h2 className="font-manrope font-bold text-2xl sm:text-3xl text-navy-950 mb-2">
          {t('step2.title')}
        </h2>
        <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">
          {t('step2.subtitle')}
        </p>
        
        <div className="space-y-4 sm:space-y-6">
          {/* Customer Information */}
          <div className="bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
            <h3 className="font-manrope font-semibold text-base sm:text-lg text-navy-900 mb-3 sm:mb-4">
              {t('step2.passenger_info')}
            </h3>
            
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                  <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                  {t('step2.full_name')} *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  placeholder="Juan Pérez"
                  className={`w-full h-11 sm:h-12 px-3 sm:px-4 text-sm sm:text-base bg-white border rounded-lg transition-all outline-none
                    ${errors.full_name ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-slate-200 focus:border-navy-500 focus:ring-navy-200'}
                    focus:ring-2`}
                  data-testid="fullname-input"
                />
                {errors.full_name && <p className="text-sm text-red-600 mt-1">{errors.full_name}</p>}
              </div>
              
              <div>
                <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                  {t('step2.email')} *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="juan@ejemplo.com"
                  className={`w-full h-11 sm:h-12 px-3 sm:px-4 text-sm sm:text-base bg-white border rounded-lg transition-all outline-none
                    ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-slate-200 focus:border-navy-500 focus:ring-navy-200'}
                    focus:ring-2`}
                  data-testid="email-input"
                />
                {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                  {t('step2.confirm_email')} *
                </label>
                <input
                  type="email"
                  value={formData.confirm_email}
                  onChange={(e) => setFormData({...formData, confirm_email: e.target.value})}
                  placeholder={t('step2.repeat_email')}
                  onPaste={(e) => e.preventDefault()}
                  className={`w-full h-11 sm:h-12 px-3 sm:px-4 text-sm sm:text-base bg-white border rounded-lg transition-all outline-none
                    ${errors.confirm_email ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-slate-200 focus:border-navy-500 focus:ring-navy-200'}
                    focus:ring-2`}
                  data-testid="confirm-email-input"
                />
                {errors.confirm_email && <p className="text-sm text-red-600 mt-1">{errors.confirm_email}</p>}
              </div>
              
              <div>
                <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                  {t('step2.phone')} *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 15);
                    setFormData({...formData, phone: value});
                  }}
                  placeholder="18095551234"
                  maxLength={15}
                  className={`w-full h-11 sm:h-12 px-3 sm:px-4 text-sm sm:text-base bg-white border rounded-lg transition-all outline-none
                    ${errors.phone ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-slate-200 focus:border-navy-500 focus:ring-navy-200'}
                    focus:ring-2`}
                  data-testid="phone-input"
                />
                <p className={`text-xs mt-1 ${formData.phone.length >= 10 ? 'text-green-600' : 'text-slate-500'}`}>{formData.phone.length}/10–15 {t('step2.digits')}</p>
                {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                  {t('step2.confirm_phone')} *
                </label>
                <input
                  type="tel"
                  value={formData.confirm_phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 15);
                    setFormData({...formData, confirm_phone: value});
                  }}
                  placeholder="18095551234"
                  maxLength={15}
                  onPaste={(e) => e.preventDefault()}
                  className={`w-full h-11 sm:h-12 px-3 sm:px-4 text-sm sm:text-base bg-white border rounded-lg transition-all outline-none
                    ${errors.confirm_phone ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-slate-200 focus:border-navy-500 focus:ring-navy-200'}
                    focus:ring-2`}
                  data-testid="confirm-phone-input"
                />
                {errors.confirm_phone && <p className="text-sm text-red-600 mt-1">{errors.confirm_phone}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                    <Plane className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                    {t('step2.flight_number')}
                  </label>
                  <input
                    type="text"
                    value={formData.flight_number}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10);
                      setFormData({...formData, flight_number: value});
                    }}
                    placeholder={t('step2.flight_number_placeholder')}
                    maxLength={10}
                    className={`w-full h-11 sm:h-12 px-3 sm:px-4 text-sm sm:text-base bg-white border rounded-lg transition-all outline-none
                      ${errors.flight_number ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-slate-200 focus:border-navy-500 focus:ring-navy-200'}
                      focus:ring-2`}
                    data-testid="flight-number-input"
                  />
                  {errors.flight_number && <p className="text-sm text-red-600 mt-1">{errors.flight_number}</p>}
                </div>

                <div>
                  <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                    <Luggage className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
                    {t('step2.luggage_count')} *
                  </label>
                  <input
                    type="number"
                    value={formData.luggage_count}
                    onChange={(e) => setFormData({...formData, luggage_count: e.target.value})}
                    placeholder={t('step2.luggage_count_placeholder')}
                    min={0}
                    max={20}
                    className={`w-full h-11 sm:h-12 px-3 sm:px-4 text-sm sm:text-base bg-white border rounded-lg transition-all outline-none
                      ${errors.luggage_count ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-slate-200 focus:border-navy-500 focus:ring-navy-200'}
                      focus:ring-2`}
                    data-testid="luggage-count-input"
                  />
                  {errors.luggage_count && <p className="text-sm text-red-600 mt-1">{errors.luggage_count}</p>}
                </div>
              </div>
            </div>
          </div>
          
          {/* Addons */}
          <div className="bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
            <h3 className="font-manrope font-semibold text-base sm:text-lg text-navy-900 mb-3 sm:mb-4">
              {t('step2.additional_services')}
            </h3>
            
            <div className="space-y-2 sm:space-y-3">
              {availableAddons.map(addon => {
                const currentQty = getAddonQuantity(addon.id);
                const minQty = addon.min_qty || 0;
                const maxQty = addon.max_qty || 999;
                const addonType = addon.type || 'PER_TRIP';
                
                return (
                  <div key={addon.id} className="flex items-center justify-between bg-white rounded-lg p-3 sm:p-4 border border-slate-200">
                    {addon.image_url && (
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 mr-3">
                        <img
                          src={addon.image_url}
                          alt={addon.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0 mr-2">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                        <p className="font-medium text-sm sm:text-base text-slate-900 truncate">{addon.name}</p>
                        <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 bg-blue-50 text-blue-700 rounded whitespace-nowrap">
                          {addonType === 'PER_PERSON' ? t('step2.per_person') :
                           addonType === 'PER_UNIT' ? t('step2.per_unit') : t('step2.per_trip')}
                        </span>
                      </div>
                      {addon.description && <p className="text-[10px] sm:text-xs text-slate-500 mb-1 line-clamp-2">{addon.description}</p>}
                      <p className="text-xs sm:text-sm text-navy-700 font-semibold">${addon.price} USD</p>
                    </div>
                    
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                      <button
                        onClick={() => handleAddonChange(addon, currentQty - 1)}
                        disabled={currentQty === 0}
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-100 hover:bg-slate-200
                          flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`addon-minus-${addon.id}`}
                      >
                        <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      <span className="w-6 sm:w-8 text-center font-medium text-sm sm:text-base">{currentQty}</span>
                      <button
                        onClick={() => handleAddonChange(addon, currentQty === 0 ? Math.max(1, minQty) : Math.min(maxQty, currentQty + 1))}
                        disabled={currentQty >= maxQty}
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-indigo-100 hover:bg-indigo-200 
                          flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`addon-plus-${addon.id}`}
                      >
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4 text-navy-800" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Notes */}
          <div>
            <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
              <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-navy-700" />
              {t('step2.notes_label')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('step2.notes_placeholder')}
              rows={4}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-slate-200 rounded-lg 
                focus:border-navy-500 focus:ring-2 focus:ring-navy-200 
                transition-all outline-none resize-none"
              data-testid="notes-textarea"
            />
          </div>
        </div>
      </div>
    </WizardLayout>
  );
};
